import { useState } from 'react';
import { Star } from 'lucide-react';

import { useSubmitFeedback } from '@/lib/hooks/useLms';
import { usePreviewStore } from '@/stores/previewStore';
import type { TopicFeedback } from '@/lib/api/lms';

interface FeedbackSectionProps {
  topicId: string;
  feedback?: TopicFeedback | null;
}

export function FeedbackSection({ topicId, feedback }: FeedbackSectionProps) {
  const [rating, setRating] = useState(feedback?.rating ?? 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(feedback?.comment ?? '');
  const submit = useSubmitFeedback(topicId);
  const previewActive = usePreviewStore((s) => s.active);

  const handleSubmit = async () => {
    if (rating < 1) return;
    await submit.mutateAsync({ rating, comment: comment.trim() });
  };

  const submitted = !!feedback;
  const dirty = rating !== (feedback?.rating ?? 0) || comment.trim() !== (feedback?.comment ?? '');

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="font-heading text-base font-bold">Rate your trainer</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        How was the teaching for this topic? Only your program admin can see this —
        your trainer and other students cannot.
      </p>

      {/* Star rating */}
      <div className="mt-4 flex items-center gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={rating === n}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="rounded p-0.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <Star
              className={`h-7 w-7 transition-colors ${
                n <= (hover || rating)
                  ? 'fill-amber-400 text-amber-400'
                  : 'fill-transparent text-muted-foreground/40'
              }`}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-2 text-sm font-medium text-foreground">{rating}/5</span>
        )}
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Share what went well or what could improve (optional)…"
        rows={3}
        maxLength={1000}
        className="mt-3 w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
      />

      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {submitted && !dirty ? 'Your feedback is saved. You can update it anytime.' : ''}
        </span>
        <button
          onClick={handleSubmit}
          disabled={submit.isPending || rating < 1 || (submitted && !dirty) || previewActive}
          title={previewActive ? 'Not available in student preview' : undefined}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {submit.isPending ? 'Saving…' : submitted ? 'Update feedback' : 'Submit feedback'}
        </button>
      </div>
    </section>
  );
}
